// ————————————————————————————————————————————————————————————
// CommandPalette.jsx — Cmd+K / Ctrl+K — Buscar pacientes
// ————————————————————————————————————————————————————————————

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Users } from 'lucide-react';
import { apiFetch } from '../api';
import { calcEdad, getAvatarColor, getIniciales } from '../utils/formato';

// ———————————————————————————————————————
// Resaltado de la coincidencia de búsqueda
// ———————————————————————————————————————
const matchStyle = {
  color: 'var(--brand-p-readable, #006a62)',
  fontWeight: 700,
  background: 'rgba(0,106,98,0.12)',
  borderRadius: 4,
  padding: '0 2px',
  boxDecorationBreak: 'clone',
  WebkitBoxDecorationBreak: 'clone',
};
function resaltar(texto, query) {
  const t = texto == null ? '' : String(texto);
  const q = (query || '').trim();
  if (!q || !t) return t;
  const idx = t.toLowerCase().indexOf(q.toLowerCase());
  if (idx === -1) return t;
  return (
    <>
      {t.slice(0, idx)}
      <span style={matchStyle}>{t.slice(idx, idx + q.length)}</span>
      {t.slice(idx + q.length)}
    </>
  );
}

// ———————————————————————————————————————
// Estilos (CSS vars del design system)
// ———————————————————————————————————————
const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(15,23,30,0.44)',
    backdropFilter: 'blur(3px)',
    WebkitBackdropFilter: 'blur(3px)',
    zIndex: 9998,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: 'min(18vh, 150px)',
    animation: 'cf-fade-in 0.14s ease both',
  },
  panel: {
    background: 'var(--bg-surface)',
    borderRadius: 18,
    boxShadow:
      '0 28px 70px -16px rgba(15,23,30,0.45), 0 4px 14px -6px rgba(15,23,30,0.22), 0 0 0 1px rgba(15,23,30,0.06)',
    width: 'min(600px, calc(100vw - 32px))',
    maxHeight: 'min(500px, calc(100vh - 200px))',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: 'cf-scale-in 0.2s cubic-bezier(0.34, 1.4, 0.64, 1) both',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '15px 16px',
    borderBottom: '1px solid var(--border)',
  },
  searchIcon: {
    flexShrink: 0,
    color: 'var(--text-3)',
  },
  input: {
    flex: 1,
    minWidth: 0,
    background: 'transparent',
    border: 'none',
    outline: 'none',
    fontSize: 16,
    fontFamily: 'var(--font-sans)',
    fontWeight: 500,
    color: 'var(--text-1)',
    lineHeight: '24px',
    height: 24,
  },
  clearBtn: {
    flexShrink: 0,
    width: 22,
    height: 22,
    borderRadius: 7,
    border: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-elevated)',
    color: 'var(--text-3)',
    cursor: 'pointer',
    transition: 'background 0.12s ease, color 0.12s ease',
  },
  shortcutBadge: {
    flexShrink: 0,
    padding: '3px 8px',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-3)',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 6,
    lineHeight: '16px',
  },
  results: {
    flex: 1,
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: '6px 8px 8px',
  },
  sectionLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 7,
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.11em',
    color: 'var(--text-3)',
    padding: '10px 10px 6px',
    userSelect: 'none',
  },
  sectionCount: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: 'var(--text-3)',
    background: 'var(--bg-elevated)',
    borderRadius: 99,
    padding: '1px 7px',
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    gap: 13,
    padding: '9px 12px',
    cursor: 'pointer',
    transition: 'background 0.12s ease',
    borderRadius: 12,
    userSelect: 'none',
  },
  itemActive: {
    background: 'var(--bg-hover)',
  },
  itemAvatar: {
    flexShrink: 0,
    width: 40,
    height: 40,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontSize: 14,
    fontWeight: 800,
    fontFamily: 'var(--font-display)',
    letterSpacing: '0.01em',
  },
  itemText: {
    flex: 1,
    minWidth: 0,
  },
  itemName: {
    fontSize: 14.5,
    fontWeight: 600,
    color: 'var(--text-1)',
    lineHeight: '20px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemMeta: {
    marginTop: 3,
    fontSize: 12,
    color: 'var(--text-3)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    lineHeight: '16px',
  },
  metaDot: {
    margin: '0 7px',
    opacity: 0.5,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    gap: 18,
    padding: '10px 16px',
    borderTop: '1px solid var(--border)',
    fontSize: 11,
    color: 'var(--text-3)',
    userSelect: 'none',
    flexShrink: 0,
  },
  footerKbd: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 18,
    height: 18,
    padding: '0 4px',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'var(--font-sans)',
    color: 'var(--text-3)',
    background: 'var(--bg-elevated)',
    border: '1px solid var(--border)',
    borderRadius: 4,
    lineHeight: 1,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 12,
    padding: '44px 24px',
    textAlign: 'center',
  },
  emptyIconWrap: {
    width: 48,
    height: 48,
    borderRadius: 14,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'var(--bg-elevated)',
    color: 'var(--text-3)',
  },
  emptyTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: 'var(--text-2)',
  },
  emptyHint: {
    fontSize: 12.5,
    color: 'var(--text-3)',
    maxWidth: 280,
    lineHeight: 1.5,
  },
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 9,
    padding: '28px 16px',
    color: 'var(--text-3)',
    fontSize: 13,
  },
  spinner: {
    width: 15,
    height: 15,
    border: '2px solid var(--border)',
    borderTopColor: 'var(--brand-s)',
    borderRadius: '50%',
    animation: 'cf-spin 0.6s linear infinite',
  },
};

// ———————————————————————————————————————
// Componente principal
// ———————————————————————————————————————
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const [pacientes, setPacientes] = useState([]);
  const [loadingPacientes, setLoadingPacientes] = useState(false);

  const inputRef = useRef(null);
  const listRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // ——— Abrir / Cerrar con Cmd+K / Ctrl+K ———
  useEffect(() => {
    function handleKeyDown(e) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, []);

  // ——— Focus input al abrir ———
  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setPacientes([]);
      setLoadingPacientes(false);
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [open]);

  // ——— Buscar pacientes con debounce ———
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setPacientes([]);
      setLoadingPacientes(false);
      return;
    }

    setLoadingPacientes(true);
    debounceRef.current = setTimeout(() => {
      apiFetch(`/pacientes/?buscar=${encodeURIComponent(trimmed)}`)
        .then((data) => {
          setPacientes((data || []).slice(0, 8));
          setLoadingPacientes(false);
        })
        .catch(() => {
          setPacientes([]);
          setLoadingPacientes(false);
        });
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query]);

  // ——— Lista de resultados ———
  const flatItems = pacientes.map((p, i) => ({
    type: 'paciente',
    data: p,
    sectionStart: i === 0 ? 'Pacientes' : null,
  }));

  // ——— Clamp activeIdx ———
  useEffect(() => {
    setActiveIdx((prev) => {
      if (flatItems.length === 0) return 0;
      return Math.min(prev, flatItems.length - 1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pacientes.length]);

  // ——— Seleccionar un item ———
  const selectItem = useCallback(
    (item) => {
      if (!item) return;
      setOpen(false);
      navigate(`/pacientes/${item.data.id}`);
    },
    [navigate]
  );

  // ——— Keyboard navigation ———
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIdx((prev) => {
          const next = prev + 1 >= flatItems.length ? 0 : prev + 1;
          scrollIntoView(next);
          return next;
        });
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIdx((prev) => {
          const next = prev - 1 < 0 ? flatItems.length - 1 : prev - 1;
          scrollIntoView(next);
          return next;
        });
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[activeIdx];
        selectItem(item);
        return;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatItems, activeIdx, selectItem]
  );

  function scrollIntoView(idx) {
    requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) return;
      const el = list.querySelector(`[data-cmd-idx="${idx}"]`);
      if (el) el.scrollIntoView({ block: 'nearest' });
    });
  }

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) setOpen(false);
  }, []);

  if (!open) return null;

  const hasQuery = query.trim().length > 0;
  const noResults = flatItems.length === 0 && !loadingPacientes;
  const showLoading = loadingPacientes && pacientes.length === 0 && hasQuery;

  return (
    <div style={styles.overlay} onClick={handleOverlayClick} role="dialog" aria-modal="true" aria-label="Buscar pacientes">
      <div style={styles.panel}>
        {/* Barra de búsqueda */}
        <div style={styles.searchRow}>
          <Search size={19} strokeWidth={2} style={styles.searchIcon} />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Buscar pacientes por nombre, teléfono, DNI o email…"
            style={styles.input}
            autoComplete="off"
            spellCheck={false}
          />
          {hasQuery && (
            <button
              type="button"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              style={styles.clearBtn}
              aria-label="Borrar búsqueda"
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--border)'; e.currentTarget.style.color = 'var(--text-1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-elevated)'; e.currentTarget.style.color = 'var(--text-3)'; }}
            >
              <X size={13} strokeWidth={2.5} />
            </button>
          )}
        </div>

        {/* Resultados */}
        <div style={styles.results} ref={listRef}>
          {!hasQuery && (
            <div style={styles.empty}>
              <div style={styles.emptyIconWrap}>
                <Users size={22} strokeWidth={1.8} />
              </div>
              <div style={styles.emptyTitle}>Busca un paciente</div>
              <div style={styles.emptyHint}>
                Escribe un nombre, apellido, teléfono, DNI o email para encontrarlo al instante.
              </div>
            </div>
          )}

          {flatItems.map((item, idx) => {
            const isActive = idx === activeIdx;
            const p = item.data;
            const nombreCompleto = `${p.nombre || ''} ${p.apellidos || ''}`.trim() || 'Paciente';
            const edad = calcEdad(p.fecha_nacimiento);
            const metaPieces = [];
            if (edad) metaPieces.push(`${edad} años`);
            if (p.telefono) metaPieces.push(resaltar(p.telefono, query));
            if (p.email) metaPieces.push(resaltar(p.email, query));
            return (
              <React.Fragment key={p.id}>
                {item.sectionStart && (
                  <div style={styles.sectionLabel}>
                    <span>{item.sectionStart}</span>
                    <span style={styles.sectionCount}>{flatItems.length}</span>
                  </div>
                )}
                <div
                  data-cmd-idx={idx}
                  style={{
                    ...styles.item,
                    ...(isActive ? styles.itemActive : {}),
                  }}
                  onClick={() => selectItem(item)}
                  onMouseEnter={() => setActiveIdx(idx)}
                  role="option"
                  aria-selected={isActive}
                >
                  <div style={{
                    ...styles.itemAvatar,
                    background: getAvatarColor(nombreCompleto),
                    boxShadow: `0 4px 12px ${getAvatarColor(nombreCompleto)}40, inset 0 0 0 1px rgba(255,255,255,0.14)`,
                  }}>
                    {getIniciales(p.nombre, p.apellidos)}
                  </div>
                  <div style={styles.itemText}>
                    <div style={styles.itemName}>
                      {resaltar(nombreCompleto, query)}
                    </div>
                    <div style={styles.itemMeta}>
                      {metaPieces.length > 0
                        ? metaPieces.map((nodo, i) => (
                            <React.Fragment key={i}>
                              {i > 0 && <span style={styles.metaDot}>·</span>}
                              {nodo}
                            </React.Fragment>
                          ))
                        : 'Sin datos de contacto'}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {showLoading && (
            <div style={styles.loading}>
              <div style={styles.spinner} />
              <span>Buscando pacientes…</span>
            </div>
          )}

          {noResults && hasQuery && !showLoading && (
            <div style={styles.empty}>
              <div style={styles.emptyIconWrap}>
                <Search size={22} strokeWidth={1.8} />
              </div>
              <div style={styles.emptyTitle}>Sin resultados</div>
              <div style={styles.emptyHint}>
                No hay pacientes que coincidan con "<strong>{query.trim()}</strong>".
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
